import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import SuritLogo from "../components/brand/SuritLogo";
import { api } from "../lib/api";

// CL.4 R3 -- baja del recordatorio de cita por correo. PÚBLICA (sin login).
//
// 🔴 Abrir la página NO da de baja: los antivirus de correo abren los enlaces solos. La baja la
//    hace el POST de `/api/publico/baja-correo`, y ese POST sólo sale al PULSAR el botón.
// 🔴 El enlace lo arma `backend/recordatorio_correo.py`: `https://clinicas.surit.ai/baja-correo?t=<token>`
//    (QUERY, no fragmento). Se lee UNA vez en el inicializador, se BORRA de la barra con
//    `history.replaceState` y viaja en el CUERPO del POST (`{t}`). Ni consola ni analítica.
// 🔴 Contrato del backend: 200 `{ok, estado: baja|ya_estaba}` (los dos se muestran igual);
//    404 `BAJA_TOKEN_INVALIDO`; 422 si el token está vacío o pasa de 200 (= enlace no válido);
//    429 límite por IP.

function leerToken() {
  try {
    return new URLSearchParams(window.location.search).get("t") || "";
  } catch {
    return "";
  }
}

// Copy aprobado por Jesús (2 Oct 2026) (todos los textos de esta pantalla son provisionales).
const TEXTOS = {
  titulo: "Recordatorios por correo", // Copy aprobado por Jesús (2 Oct 2026)
  inicial: "¿Quieres dejar de recibir recordatorios de tus citas por correo de esta clínica?", // Copy aprobado por Jesús (2 Oct 2026)
  boton: "Sí, darme de baja", // Copy aprobado por Jesús (2 Oct 2026)
  exito: "Listo. Ya no recibirás recordatorios de citas por correo de esta clínica.", // Copy aprobado por Jesús (2 Oct 2026)
  invalido: "Enlace no válido.", // Copy aprobado por Jesús (2 Oct 2026)
  limite: "Demasiados intentos. Intenta más tarde.", // Copy aprobado por Jesús (2 Oct 2026)
  error: "No pudimos completar la solicitud. Intenta de nuevo.", // Copy aprobado por Jesús (2 Oct 2026)
};

export default function BajaCorreo() {
  // En el inicializador y NO en el efecto: en desarrollo React ejecuta los efectos dos veces y la
  // segunda ya encontraría la barra limpia (mismo patrón que Acceso.js).
  const [token] = useState(leerToken);
  // inicial | enviando | exito | invalido | limite | error
  const [fase, setFase] = useState(() => (token ? "inicial" : "invalido"));

  useEffect(() => {
    if (window.location.search || window.location.hash) {
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  const darDeBaja = async () => {
    if (!token || fase === "enviando") return;
    setFase("enviando");
    try {
      await api.post("/publico/baja-correo", { t: token });
      setFase("exito");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404 || status === 422) setFase("invalido");
      else if (status === 429) setFase("limite");
      else setFase("error");
    }
  };

  const conBoton = fase === "inicial" || fase === "enviando" || fase === "limite" || fase === "error";
  const mensaje = {
    inicial: TEXTOS.inicial,
    enviando: TEXTOS.inicial,
    exito: TEXTOS.exito,
    invalido: TEXTOS.invalido,
    limite: TEXTOS.limite,
    error: TEXTOS.error,
  }[fase];
  const esAviso = fase === "limite" || fase === "error" || fase === "invalido";

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-card p-8" data-testid="baja-correo" data-fase={fase}>
        <SuritLogo size={42} tone="claro" rotulo label="Surit Clínicas" />
        <div className="mt-6 space-y-4">
          <h1 className="font-display text-2xl font-bold">{TEXTOS.titulo}</h1>
          {esAviso ? (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert" data-testid="baja-correo-mensaje">
              {mensaje}
            </div>
          ) : (
            <p className={`text-sm ${fase === "exito" ? "text-slate-800" : "text-slate-600"}`} role={fase === "exito" ? "status" : undefined} data-testid="baja-correo-mensaje">
              {mensaje}
            </p>
          )}
          {conBoton && (
            <button type="button" onClick={darDeBaja} disabled={fase === "enviando"}
              className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 text-white font-medium py-3 rounded-xl hover:bg-blue-700 disabled:opacity-60"
              data-testid="baja-correo-boton">
              {fase === "enviando" && <Loader2 className="w-4 h-4 animate-spin" />}
              {TEXTOS.boton}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
